import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import pick from 'lodash/pick';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';

import {propertiesUtils, renamePropertyValue} from 'common/report/util/properties';
import {pluralize} from 'common/util/string';
import {formatDate, parseDate} from 'common/util/date';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {AbsoluteDate, DateRangeType, SpecialTimeUnit, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {CohortSegment} from 'common/query-builder/query-block/segment-menu/types';
import {PropertyFilterDateRange} from 'common/widgets/date-range-picker/models/property-filter-date-range';
import {reporter} from 'common/report/error-reporting';
import {MappedPropertyValues, PropertyType, ResourceType} from 'common/report/constants';
import {MPProperties} from 'common/constants';
import type {Behavior, CustomProperty, ProfileHistoryOptions, Segmentation} from 'common/types/reports/query';
import {FUNNELS_TIME_TO_CONVERSION, PAGE_DURATION} from 'common/query-builder/query-block/properties-menu/constants';

import {isEnhancedListFilter} from '../utils';
import {getOperatorFromFilterOperator, getTypeCastOperatorFromType} from './selector-tree/utils';
import {
  BooleanOperator,
  CohortOperator,
  FilterOperand,
  FilterOperator,
  FilterValue,
  ListOperator,
  ListQuantifier,
  LogicalOperator,
  NthTimeOperator,
  NumberOperator,
  ObjectOperator,
  PropertyTypeSet,
  StringOperator,
} from './types';
import {
  ArithmeticOperator,
  Operator,
  SerializedFilterOperand,
  SerializedFilterValue,
  SerializedResourceType,
} from './selector-tree/types';
import {ExpressionNode} from './selector-tree/expression-node';
import {OperandNode} from './selector-tree/operand-node';
import {OperatorNode} from './selector-tree/operator-node';

const MAX_LABEL_ENTRIES = 3;

export const DEFAULT_FILTER_OPERATOR: {[key in PropertyType]?: FilterOperator} = {
  [PropertyType.Boolean]: BooleanOperator.True,
  [PropertyType.List]: ListOperator.Contains,
  [PropertyType.Number]: NumberOperator.EqualTo,
  [PropertyType.String]: StringOperator.Equals,
  [PropertyType.Object]: ObjectOperator.Set,
};

export const DEFAULT_FILTER_VALUE: {[key in PropertyType]?: FilterValue} = {
  [PropertyType.Boolean]: true,
  [PropertyType.List]: [],
  [PropertyType.Number]: 1,
  [PropertyType.String]: null,
  [PropertyType.Object]: null,
};

interface LabelPart {
  highlight: boolean;
  label: string;
}

const highlightPart = (label: string): LabelPart => ({highlight: true, label});
const plainPart = (label: string): LabelPart => ({highlight: false, label});

const PROPERTY_FILTER_ATTRIBUTES: Array<keyof Segmentation> = [
  `behavior`,
  `customProperty`,
  `customPropertyId`,
  `datasetId`,
  `dataGroupId`,
  `tempDataGroupId`,
  `resourceType`,
  `propertyName`,
  `propertyObjectKey`,
  `propertyDefaultType`,
  `joinPropertyType`,
  `listQuantifier`,
  `listItemFilters`,
  `propertyType`,
  `filterOperator`,
  `filterValue`,
  `filterDateUnit`,
  `profileHistoryOptions`,
  `scdEvent`,
  `startStepNumber`,
  `endStepNumber`,
];

/** Defines fields that specify the actual filter value to apply on a property */
export const FILTER_VALUE_ATTRIBUTES = [`filterValue`, `filterOperator`, `filterDateUnit`, `listQuantifier`] as const;

export class PropertyFilter implements Segmentation {
  public static getCombinedLabel(filters: PropertyFilter[], separator = `and`, limit = 5) {
    const size = Math.min(filters.length, limit);

    let label = filters
      .slice(0, size)
      .map((filter) => `${filter.getLabel()}`)
      .join(` ${separator} `);

    if (filters.length > limit) {
      const diff = filters.length - limit;
      label += ` and ${diff} more ${pluralize(`filter`, diff)}`;
    }

    return label;
  }

  public propertyType: PropertyType;
  public propertyName: string | string[];
  public filterOperator: FilterOperator;
  public filterValue: FilterValue = null;
  public renamed: string;
  public scdEvent: string;

  public customPropertyId?: number | string;
  public customProperty?: CustomProperty;
  public datasetId?: string;
  public dataGroupId?: string;
  public tempDataGroupId?: string;
  public resourceType: ResourceType;
  /** defines the selected key for Object typed filters */
  public propertyObjectKey?: string | string[];
  public propertyDefaultType: PropertyType;
  public joinPropertyType?: PropertyType;
  public listQuantifier?: ListQuantifier;
  public listItemFilters?: PropertyFilter[];
  public filterDateUnit?: TimeUnit | SpecialTimeUnit;
  public behavior?: Behavior;
  public profileHistoryOptions?: ProfileHistoryOptions;
  public startStepNumber?: number;
  public endStepNumber?: number;

  /**
   * Used to track hierarchy of nested property filters.
   * This is managed by its parent and should not be set externally.
   *
   * This is set to non-enumerable in setParent() to prevent circular reference error when serialized by JSON.stringify
   */
  private _parent?: PropertyFilter;

  constructor(attributes: Partial<PropertyFilter> = {}) {
    Object.assign(this, attributes);
    if (this.hasSelectedProperty() && !this.filterOperator) {
      Object.assign(
        this,
        this.getDefaultFilterAttributes({
          propertyType: attributes.propertyType,
          propertyObjectKey: attributes.propertyObjectKey,
        }),
      );
    }

    if (this.propertyType === PropertyType.Datetime && !(this.filterValue instanceof DateRange)) {
      this.filterValue = this.createDateRange(this.filterValue as any);
    }

    if (!this.filterDateUnit && this.supportsFilterUnit()) {
      this.filterDateUnit = TimeUnit.Day;
    }

    if (
      this.joinPropertyType === PropertyType.List &&
      this.resourceType !== ResourceType.Cohort &&
      !this.listQuantifier
    ) {
      this.listQuantifier = ListQuantifier.Any;
    }

    // make sure any list item filters are set up
    if (this.listItemFilters) {
      this.listItemFilters = this.listItemFilters.map((filter) => {
        const instance = filter instanceof PropertyFilter ? filter : new PropertyFilter(filter);
        instance.setParent(this);
        return instance;
      });
    }

    // Transform object properties to new format
    this.transformObjectProperties();

    if (Number.isInteger(this.dataGroupId) || Number.isInteger(this.tempDataGroupId)) {
      // dataGroupId gets truncated when coerced to a number. trying to figure out when this happens and possibly prevent regressions
      // the resulting query will fail, so we may want to make a best-effort to make this a non-dimension table property filter if we see a lot of these errors
      reporter.error(
        new TypeError(
          `[lookup] dataGroupId/tempDataGroupId is int not string. attributes: ${JSON.stringify(attributes)}`,
        ),
      );
    }
  }

  private transformObjectProperties() {
    let type = PropertyType.String;
    if (this.propertyType === PropertyType.Object && typeof this.propertyObjectKey === `string`) {
      const filterValue = Array.isArray(this.filterValue) ? this.filterValue[0] : this.filterValue;

      if (filterValue === null) {
        // old object filters with a null value defined a `is set` filter
        type = PropertyType.String;
        this.filterOperator = StringOperator.Set;
        this.filterValue = null;
      } else if (filterValue === true || filterValue === false) {
        type = PropertyType.Boolean;
        this.filterOperator = filterValue ? BooleanOperator.True : BooleanOperator.False;
        this.filterValue = filterValue;
      } else if (this.propertyName === MPProperties.Experiments) {
        type = PropertyType.String;
        this.filterOperator = StringOperator.Equals;
        this.filterValue = [filterValue.toString()];
      } else if (typeof filterValue === `number`) {
        type = PropertyType.Number;
        this.filterOperator = NumberOperator.EqualTo;
        this.filterValue = filterValue;
      }

      this.propertyType = type;
      this.propertyDefaultType = type;
      this.propertyObjectKey = [this.propertyObjectKey];
      this.joinPropertyType = PropertyType.Object;
    }
  }

  setParent(filter: PropertyFilter) {
    // setting _parent like this each time prevents a JSON.stringify circular reference error if it accidentally becomes enumerable
    Object.defineProperty(this, `_parent`, {value: filter, enumerable: false, writable: true});
  }

  getParent() {
    return this._parent;
  }

  public createDateRange(attributes: Partial<DateRange> = {}): DateRange {
    return new PropertyFilterDateRange(attributes);
  }

  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  public getExpressionTree(): OperatorNode {
    let expression = null;
    const type =
      this.joinPropertyType === PropertyType.Object && Array.isArray(this.propertyObjectKey)
        ? PropertyType.Object
        : this.propertyType;
    if (type === PropertyType.Datetime) {
      expression = this.getValueOperatorNode(
        ArithmeticOperator.Datetime,
        (this.filterValue as DateRange).getCohortApiFormat(),
      );
      const dateRangeType = this.filterOperator as DateRangeType;
      if ([DateRangeType.NotBetween, DateRangeType.NotOn, DateRangeType.NotRelativeAfter].includes(dateRangeType)) {
        expression = new OperatorNode(LogicalOperator.Not, expression, null);
      }
      // set/notset operators
    } else if (
      this.filterOperator === ObjectOperator.Set ||
      this.filterOperator === StringOperator.Set ||
      this.filterOperator === StringOperator.NotSet
    ) {
      // no value provided for set/not set operators, and no typecast
      // typecasting an undefined value to string yields the string "undefined", which is always defined
      expression = new OperatorNode(
        getOperatorFromFilterOperator(this.filterOperator),
        new OperandNode({
          property: this.resourceType,
          value: this.getPropertyNodeValue(),
          data_group_id: this.dataGroupId,
          temp_data_group_id: this.tempDataGroupId,
          ...(this.scdEvent ? {scdEvent: this.scdEvent} : {}),
          ...(this.dataGroupId || this.tempDataGroupId ? {join_property_type: this.joinPropertyType} : {}),
        }),
        null,
      );
    } else if (Array.isArray(this.filterValue)) {
      switch (type) {
        case PropertyType.Number: {
          let greaterThan: SerializedFilterOperand = null;
          let lessThan: SerializedFilterOperand = null;
          if (type === PropertyType.Number) {
            greaterThan = Math.min(...(this.filterValue as number[]));
            lessThan = Math.max(...(this.filterValue as number[]));
          } else {
            const [first, second] = this.filterValue.map((v) => parseDate(v.toString()));
            greaterThan = formatDate(first < second ? first : second, {iso: true});
            lessThan = formatDate(first < second ? second : first, {iso: true});
          }
          expression = this.getBetweenOperatorExpressionTree(greaterThan, lessThan);
          if (this.filterOperator === NumberOperator.NotBetween) {
            expression = new OperatorNode(LogicalOperator.Not, expression, null);
          }
          break;
        }
        case PropertyType.Object:
        case PropertyType.String: {
          const isEquals =
            this.filterOperator === StringOperator.Equals || this.filterOperator === ObjectOperator.Equals;

          if (!this.filterValue.length || (!isEquals && this.filterOperator !== StringOperator.DoesNotEqual)) {
            throw new Error(`unexpected value array`);
          }

          // string is equal to or not equal to an array of values
          const logicalOperator = isEquals ? LogicalOperator.Or : LogicalOperator.And;
          const operator = getOperatorFromFilterOperator(this.filterOperator);
          expression = this.filterValue.slice(1).reduce((root, value) => {
            const subTree = this.getValueOperatorNode(operator, value);
            return new OperatorNode(logicalOperator, root, subTree);
          }, this.getValueOperatorNode(operator, this.filterValue[0]));
          break;
        }
        default: {
          throw new Error(`unexpected value array`);
        }
      }
    } else {
      expression = this.getValueOperatorNode(getOperatorFromFilterOperator(this.filterOperator), this.filterValue);
    }
    return expression;
  }

  public hasSelectedProperty(): boolean {
    // cohort filters do not rely on a property, we skip the
    // propertyName check since it behaves as a list filter
    if (this.resourceType === ResourceType.Cohort) {
      return true;
    }

    return !!(this.resourceType && this.propertyType && (this.behavior || this.propertyName));
  }

  public getAttributes(): Segmentation {
    const attributes = pick(this, PROPERTY_FILTER_ATTRIBUTES) as Segmentation;

    // SS does not always ensure filterValue is populated so we need to have an additional check here
    if (this.propertyType === PropertyType.Datetime && this.filterValue) {
      attributes.filterValue = (this.filterValue as DateRange).getAttributes();
    }

    return attributes;
  }

  public equals(otherFilter: PropertyFilter): boolean {
    const primitiveKeys: Array<keyof PropertyFilter> = [
      `resourceType`,
      `dataGroupId`,
      `tempDataGroupId`,
      `propertyName`,
      `propertyDefaultType`,
      `propertyObjectKey`,
      `propertyType`,
      `joinPropertyType`,
      `listQuantifier`,
      `filterOperator`,
      `scdEvent`,
    ];

    for (const key of primitiveKeys) {
      const mine = this[key];
      const other = otherFilter[key];
      if (notDefined(mine) && notDefined(other)) {
        continue;
      } else if (!isEqual(mine, other)) {
        // compare via isEqual because "propertyName" may be an array (for lookup table properties)
        return false;
      }
    }

    switch (this.propertyType) {
      case PropertyType.String:
        switch (this.filterOperator) {
          case StringOperator.Equals:
          case StringOperator.DoesNotEqual: {
            const thisFilterValues = [...(this.filterValue as FilterOperand[])].sort();
            const otherFilterValues = [...(otherFilter.filterValue as FilterOperand[])].sort();
            return isEqual(thisFilterValues, otherFilterValues);
          }
          default:
            return isEqual(this.filterValue, otherFilter.filterValue);
        }
      case PropertyType.Datetime:
        return (this.filterValue as DateRange).equals(otherFilter.filterValue as DateRange);
      default:
        return isEqual(this.filterValue, otherFilter.filterValue);
    }
  }

  public isValid(): boolean {
    const propertyType = this.propertyType;
    if (!PropertyTypeSet.has(propertyType)) {
      return false;
    }

    const isComplexListFilter = !!this.listQuantifier && this.propertyType === PropertyType.Object;
    if (isComplexListFilter) {
      // must have at least one valid list item filter
      return !!this.listItemFilters?.length && this.listItemFilters.every((filter) => filter.isValid());
    }

    if (propertyType !== PropertyType.Datetime && !this.getFilterOperators().includes(this.filterOperator)) {
      return false;
    }

    switch (propertyType) {
      case PropertyType.Object:
        return (
          // key + array value is `key value equals`
          (this.propertyObjectKey &&
            Array.isArray(this.filterValue) &&
            this.filterValue.every((value) => !isNil(value))) ||
          // key + null value is `key is set`
          (this.propertyObjectKey !== null && this.filterValue === null)
        );
      case PropertyType.String:
        switch (this.filterOperator) {
          case StringOperator.Contains:
          case StringOperator.DoesNotContain:
            return (
              Object.prototype.hasOwnProperty.call(this, `filterValue`) &&
              isString(this.filterValue) &&
              !isEmpty(this.filterValue)
            );
          case StringOperator.Equals:
          case StringOperator.DoesNotEqual:
            return (
              Array.isArray(this.filterValue) &&
              this.filterValue.length > 0 &&
              this.filterValue.every((val) => typeof val === `string`)
            );
          case StringOperator.Set:
          case StringOperator.NotSet:
            return isNil(this.filterValue);
          default:
            break;
        }
        break;
      case PropertyType.Datetime:
        return this.filterValue instanceof DateRange && this.filterValue.isValid();
      case PropertyType.Number:
        switch (this.filterOperator) {
          case NumberOperator.Set:
          case NumberOperator.NotSet:
            return true;
          case NumberOperator.Between:
          case NumberOperator.NotBetween:
            return Array.isArray(this.filterValue) && this.filterValue.length === 2 && this.filterValue.every(isNumber);
          default:
            return isNumber(this.filterValue);
        }
      case PropertyType.List:
        return !!this.filterValue;
      case PropertyType.Boolean:
        return (
          (this.filterOperator === BooleanOperator.True && this.filterValue === true) ||
          (this.filterOperator === BooleanOperator.False && this.filterValue === false)
        );
      default:
        break;
    }
    return true;
  }

  public clonePropertyFilter(newAttributes: Partial<PropertyFilter>): this {
    return new (this.constructor as any)({
      ...this.getAttributes(),
      ...newAttributes,
    });
  }

  public setDateRange(dateRange: DateRange): this {
    return this.clonePropertyFilter({
      filterOperator: dateRange.type,
      filterValue: dateRange,
    });
  }

  public setAttribute(key: keyof PropertyFilter, value: any): this {
    switch (key) {
      case `propertyType`: {
        return this.clonePropertyFilter({
          propertyType: value,
          ...this.getDefaultFilterAttributes({
            propertyType: value,
            propertyObjectKey: this.propertyObjectKey,
          }),
        });
      }
      case `filterOperator`: {
        return this.clonePropertyFilter({
          ...this.getDefaultFilterAttributes({
            filterOperator: value,
            propertyType: this.propertyType,
            propertyObjectKey: this.propertyObjectKey,
          }),
        });
      }
      case `propertyObjectKey`: {
        return this.clonePropertyFilter({
          filterOperator: StringOperator.Set,
          filterValue: null,
          propertyObjectKey: value,
        });
      }
      case `filterValue`: {
        // object properties without a defined key value are defined as a 'is set' filter
        const isObject = this.propertyType === PropertyType.Object;
        const hasKeyValue = Array.isArray(value) && value.every((item) => !isNil(item));

        return this.clonePropertyFilter({
          filterOperator: isObject ? (hasKeyValue ? StringOperator.Equals : StringOperator.Set) : this.filterOperator,
          filterValue: value,
        });
      }
      case `listQuantifier`: {
        return this.clonePropertyFilter({
          listQuantifier: value,
        });
      }
      case `listItemFilters`: {
        return this.clonePropertyFilter({
          listItemFilters: value,
        });
      }
      case `filterDateUnit`: {
        return this.clonePropertyFilter({
          filterDateUnit: value,
        });
      }
      case `profileHistoryOptions`: {
        return this.clonePropertyFilter({
          profileHistoryOptions: value,
        });
      }
      default: {
        throw new Error(`Attribute ${key} cannot be set on PropertyFilter`);
      }
    }
  }

  public getLabel(): string {
    return this.getLabelParts()
      .map((part) => part.label)
      .join(` `);
  }

  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  public getLabelParts(): LabelPart[] {
    let operatorString = this.propertyType === PropertyType.Boolean ? `is` : this.filterOperator;
    const valueString = String(this.filterValue);
    let valueParts: LabelPart[] = [];
    const basePropertyPart = propertiesUtils.renameProperty(this.propertyName, this.resourceType, {
      dataGroupId: this.dataGroupId,
      customProperty: this.customProperty,
    });

    if (this.propertyDefaultType === PropertyType.Object) {
      const filterValue = Array.isArray(this.filterValue) ? this.filterValue[0] : null;
      valueParts = this.propertyObjectKey ? [highlightPart(this.propertyObjectKey.toString())] : [];
      const keys = Array.isArray(this.propertyObjectKey)
        ? this.propertyObjectKey
        : [this.propertyObjectKey].filter(Boolean);
      valueParts = keys.map(highlightPart);

      // object properties use other data type operators but
      // should read `object has key value`
      operatorString = `has`;

      if (this.propertyName === `$experiments` && this.propertyObjectKey) {
        const mappingPropertyName = filterValue ? `$variant_id` : `$experiment_id`;
        const mappingPropertyValue = (filterValue || this.propertyObjectKey).toString();
        valueParts = [highlightPart(renamePropertyValue(mappingPropertyValue, mappingPropertyName))];
      } else if (this.propertyObjectKey && filterValue !== null) {
        valueParts.push(plainPart(this.filterOperator), highlightPart(filterValue.toString()));
      }
    } else if (isNil(this.filterValue)) {
      // handle nth time event filters
      if (this.filterOperator === NthTimeOperator.NthTime) {
        return [highlightPart(`First time ever`)];
      }

      return [basePropertyPart, operatorString].map(highlightPart);
      // don't check if this.propertyType === PropertyType.List, it should be a safe assumption, and not all cohort filters have a propertyType
    } else if (this.resourceType === ResourceType.Cohort && Array.isArray(this.filterValue)) {
      let cohortOperator: CohortOperator = null;
      switch (this.filterOperator) {
        case ListOperator.Contains:
          cohortOperator = CohortOperator.In;
          break;
        case ListOperator.DoesNotContain:
          cohortOperator = CohortOperator.NotIn;
          break;
        default:
          throw new Error(`unexpected cohort list operator`);
      }

      const filterValue = this.filterValue as FilterOperand[];
      const suffixParts =
        filterValue.length > 1
          ? [plainPart(`or ${filterValue.length - 1} ${pluralize(`other`, filterValue.length - 1)}`)]
          : [];
      return [
        plainPart(`Users`),
        plainPart(cohortOperator.toString()),
        highlightPart((this.filterValue[0] as CohortSegment).cohort.name),
        ...suffixParts,
      ];
    } else if (Array.isArray(this.filterValue)) {
      switch (this.propertyType) {
        case PropertyType.Number: {
          // filter between two values
          const [first, second] = this.filterValue.map(String).map(highlightPart);
          valueParts = [first, plainPart(`and`), second];
          break;
        }
        case PropertyType.String: {
          const stringValues = this.filterValue as string[];
          let valueStringParts;
          if (stringValues.length > MAX_LABEL_ENTRIES) {
            valueStringParts = stringValues
              .slice(0, MAX_LABEL_ENTRIES - 1)
              .map(highlightPart)
              .concat(plainPart(`${this.filterValue.length - MAX_LABEL_ENTRIES + 1} more`));
          } else {
            valueStringParts = stringValues.map(highlightPart);
          }

          valueParts = valueStringParts.reduce((acc, part, idx, arr) => {
            return idx === arr.length - 1 && arr.length > 1 ? [...acc, plainPart(`or ${part.label}`)] : [...acc, part];
          }, []);
          break;
        }
        default:
          throw new Error(`unexpected array value`);
      }
    } else if (this.propertyType === PropertyType.Datetime) {
      const filterValue = this.filterValue as DateRange;
      switch (this.filterOperator as DateRangeType) {
        case DateRangeType.Between:
        case DateRangeType.NotBetween: {
          const {from, to} = filterValue;
          const [first, second] = [from, to]
            .map((date) => formatDate(parseDate(date as AbsoluteDate)))
            .map(highlightPart);
          valueParts = [first, plainPart(`and`), second];
          break;
        }
        case DateRangeType.On:
        case DateRangeType.NotOn:
        case DateRangeType.Since:
          valueParts = [formatDate(parseDate(filterValue.from as AbsoluteDate))].map(highlightPart);
          break;
        case DateRangeType.Before:
          valueParts = [formatDate(parseDate(filterValue.to as AbsoluteDate))].map(highlightPart);
          break;
        case DateRangeType.RelativeFuture:
        case DateRangeType.RelativeAfter:
        case DateRangeType.NotRelativeAfter:
        case DateRangeType.RelativeBefore: {
          const {value: windowValue, unit: windowUnit} = filterValue.window;
          valueParts = [
            highlightPart(`${windowValue > 1 ? `${windowValue} ` : ``}${pluralize(windowUnit, windowValue)}`),
          ];
          break;
        }
        default:
          break;
      }
    } else {
      valueParts = [highlightPart(valueString)];
    }

    const propertyValueRenameProp =
      this.propertyName === MPProperties.Experiments ? MappedPropertyValues.VariantId : this.propertyName;
    return [
      highlightPart(basePropertyPart),
      plainPart(operatorString),
      ...valueParts.map((part) => ({...part, label: renamePropertyValue(part.label, propertyValueRenameProp)})),
      ...(isEnhancedListFilter(this) ? [plainPart(`(match ${this.listQuantifier})`)] : []),
    ];
  }

  /* gets a label for just the filter value part including renames e.g. `NA countries (US, Mexico, Canada) */
  public getValuesLabel(includeRename = true): string {
    const filterValues = this.getLabelParts()
      // slicing from idx 2 skips the property name and operator (e.g. [prop1, equals, val1, val2, val3] => [val1, val2, val3])
      .slice(2)
      .map((item) => item.label);
    const filterValueStr = filterValues.join(filterValues.length > 2 ? `, ` : ` `);

    if (includeRename && this.renamed) {
      return `${this.renamed} (${filterValueStr})`;
    } else {
      return filterValueStr;
    }
  }

  public getPropertyTypes(): PropertyType[] {
    return [PropertyType.String, PropertyType.Number, PropertyType.Boolean, PropertyType.Datetime, PropertyType.List];
  }

  public getBooleanOperators(): BooleanOperator[] {
    return [BooleanOperator.True, BooleanOperator.False];
  }

  public getListOperators(): ListOperator[] {
    return [ListOperator.Contains, ListOperator.DoesNotContain];
  }

  public getNumberOperators(): NumberOperator[] {
    return [
      NumberOperator.Between,
      NumberOperator.NotBetween,
      NumberOperator.AtLeast,
      NumberOperator.AtMost,
      NumberOperator.GreaterThan,
      NumberOperator.LessThan,
      NumberOperator.EqualTo,
      NumberOperator.NotEqualTo,
      NumberOperator.Set,
      NumberOperator.NotSet,
    ];
  }

  public getStringOperators(): StringOperator[] {
    return [
      StringOperator.Contains,
      StringOperator.DoesNotContain,
      StringOperator.Equals,
      StringOperator.DoesNotEqual,
      StringOperator.Set,
      StringOperator.NotSet,
    ];
  }

  public getObjectOperators(): ObjectOperator[] {
    return [ObjectOperator.Equals, ObjectOperator.Set];
  }

  public getFilterOperators(): FilterOperator[] {
    switch (this.propertyType) {
      case PropertyType.Boolean:
        return this.getBooleanOperators();
      case PropertyType.List:
        return this.getListOperators();
      case PropertyType.Number:
        return this.getNumberOperators();
      case PropertyType.Object:
        return this.getObjectOperators();
      case PropertyType.String:
        return this.getStringOperators();
      case PropertyType.NonProperty:
        return [NthTimeOperator.NthTime];
      default:
        return null;
    }
  }

  private getPropertyNodeValue(): SerializedFilterValue {
    let propertyName = this.propertyName as SerializedFilterValue;

    if (this.customPropertyId) {
      propertyName = {customPropertyId: this.customPropertyId} as SerializedFilterValue;
    } else if (this.customProperty) {
      propertyName = {customProperty: this.customProperty} as SerializedFilterValue;
    } else if (this.propertyType === PropertyType.Object && typeof this.propertyObjectKey === `string`) {
      propertyName = [propertyName, this.propertyObjectKey] as SerializedFilterValue;
    } else if (Array.isArray(this.propertyObjectKey)) {
      propertyName = [propertyName].concat(this.propertyObjectKey) as SerializedFilterValue;
    }

    return propertyName;
  }

  private getPropertyOperandNode(): OperandNode {
    return new OperandNode({
      property: this.resourceType,
      value: this.getPropertyNodeValue(),
      data_group_id: this.dataGroupId,
      temp_data_group_id: this.tempDataGroupId,
      ...(this.dataGroupId || this.tempDataGroupId ? {join_property_type: this.joinPropertyType} : {}),
      ...(this.scdEvent ? {scdEvent: this.scdEvent} : {}),
    });
  }

  private getPropertyNode(): ExpressionNode {
    /**
     * Always apply a typecast operator in the tree, even when the user did not explicitly choose a typecast.
     * This has the benefit of capturing the property type information in the tree, allowing us to distinguish
     * between different UI representations for operators such as ==
     */
    return new OperatorNode(
      getTypeCastOperatorFromType(this.propertyType),
      new OperandNode({
        property: this.resourceType,
        value: this.getPropertyNodeValue(),
        data_group_id: this.dataGroupId,
        temp_data_group_id: this.tempDataGroupId,
        ...(this.dataGroupId || this.tempDataGroupId ? {join_property_type: this.joinPropertyType} : {}),
        ...(this.scdEvent ? {scdEvent: this.scdEvent} : {}),
      }),
      null,
    );
  }

  private getValueOperatorNode(operator: Operator, value: SerializedFilterValue) {
    if (operator === ArithmeticOperator.In || operator === ArithmeticOperator.NotIn) {
      // ensure that JQL output is "<value> in <list/string property>" by having value as left child
      return new OperatorNode(
        operator,
        new OperandNode({property: SerializedResourceType.Literal, value}),
        this.getPropertyNode(),
      );
    } else {
      const isObject =
        this.propertyType === PropertyType.Object ||
        (this.joinPropertyType === PropertyType.Object && Array.isArray(this.propertyObjectKey));
      return new OperatorNode(
        operator,
        // object properties should not define a typecast to be consistent to how they are generated in arb_selector.
        isObject ? this.getPropertyOperandNode() : this.getPropertyNode(),
        new OperandNode({property: SerializedResourceType.Literal, value}),
      );
    }
  }

  private getBetweenOperatorExpressionTree(greaterThan: SerializedFilterOperand, lessThan: SerializedFilterOperand) {
    return new OperatorNode(
      LogicalOperator.And,
      new OperatorNode(
        ArithmeticOperator.GreaterThanEquals,
        this.getPropertyNode(),
        new OperandNode({property: SerializedResourceType.Literal, value: greaterThan}),
      ),
      new OperatorNode(
        ArithmeticOperator.LessThanEquals,
        this.getPropertyNode(),
        new OperandNode({property: SerializedResourceType.Literal, value: lessThan}),
      ),
    );
  }

  private getDefaultFilterAttributes({
    propertyType,
    filterOperator,
    propertyObjectKey = null,
  }: Partial<PropertyFilter>): Partial<PropertyFilter> {
    let filterValue: FilterValue;

    if (!filterOperator && Object.prototype.hasOwnProperty.call(DEFAULT_FILTER_OPERATOR, propertyType)) {
      filterOperator = DEFAULT_FILTER_OPERATOR[propertyType];
    }

    switch (propertyType) {
      case PropertyType.Object: {
        filterValue = null;
        break;
      }
      case PropertyType.Boolean: {
        switch (filterOperator as BooleanOperator) {
          case BooleanOperator.True: {
            filterValue = true;
            break;
          }
          case BooleanOperator.False: {
            filterValue = false;
            break;
          }
          default:
            break;
        }
        break;
      }
      case PropertyType.Datetime: {
        filterValue = this.createDateRange({type: filterOperator as DateRangeType});
        filterOperator = filterValue.type;
        break;
      }
      case PropertyType.List: {
        filterValue = null;
        break;
      }
      case PropertyType.Number: {
        switch (filterOperator as NumberOperator) {
          case NumberOperator.Between:
          case NumberOperator.NotBetween: {
            filterValue = [null, null];
            break;
          }
          default: {
            filterValue = null;
          }
        }
        break;
      }
      case PropertyType.String: {
        switch (filterOperator as StringOperator) {
          case StringOperator.Equals:
          case StringOperator.DoesNotEqual: {
            filterValue = [];
            break;
          }
          default: {
            filterValue = null;
          }
        }
        break;
      }
      default:
        break;
    }

    return {filterOperator, filterValue, propertyObjectKey};
  }

  /** Gets parent filter hierarchy. */
  getParents(): PropertyFilter[] {
    const parents: PropertyFilter[] = [];

    // walk up the parents until we run out
    let currentParent = this.getParent();
    while (currentParent) {
      parents.unshift(currentParent);
      currentParent = currentParent?.getParent();
    }

    return parents;
  }

  supportsFilterUnit() {
    return (
      this.resourceType === ResourceType.Other &&
      (this.propertyName === FUNNELS_TIME_TO_CONVERSION.value || this.propertyName === PAGE_DURATION.value) &&
      this.filterOperator !== NumberOperator.Set &&
      this.filterOperator !== NumberOperator.NotSet
    );
  }
}

function notDefined(val) {
  return val === undefined || val === null;
}
